<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
/* global axios */

const props = defineProps({
  stage: { type: Object, required: true },
  pipeline: { type: Object, required: true },
  pipelineId: { type: [String, Number], required: true },
  accountId: { type: [String, Number], required: true },
  // Cantidad de leads en esta columna · usado para mostrar/ocultar la sección de rescate
  dealsInStage: { type: Number, default: 0 },
});
const emit = defineEmits(['close', 'deleted', 'updated']);

const store = useStore();

const mode = ref('current'); // 'current' | 'specific' | 'all'
const branchesToRemove = ref([]);
const confirmName = ref('');
const leadAction = ref('move_to_lost'); // 'move_to_lost' | 'move_to_stage' | 'delete'
const targetStageId = ref(null);
const saving = ref(false);
const errorMsg = ref(null);

const allBranches = computed(() => store.getters['branches/getBranches'] || []);
const activeBranch = computed(() => store.getters['branches/getActiveBranch']);

// Solo super-admin (Victor) puede borrar de otras / todas las sucursales.
// El resto (Elin/SDO, agentes, encargados) solo quita la columna de SU sucursal
// activa (Victor 2026-06-02).
const isSuperAdmin = computed(() => {
  const accountId = store.getters.getCurrentAccountId;
  const info = store.getters.getCurrentUser?.accounts?.find(
    a => Number(a.id) === Number(accountId)
  );
  return info?.super_admin === true;
});

// Pipeline scope (resuelto: si está vacío = todas las branches)
const pipelineScopeIds = computed(() => {
  const scope = props.pipeline.scope_branch_ids || [];
  if (!scope.length) return allBranches.value.map(b => b.id);
  return [...scope];
});

// Stage scope (resuelto: si vacío = todas las del pipeline)
const stageScopeIds = computed(() => {
  const scope = props.stage.scope_branch_ids || [];
  if (!scope.length) return pipelineScopeIds.value;
  return [...scope];
});

// Sucursales donde la etapa aparece (intersección stage+pipeline)
const visibleBranches = computed(() =>
  allBranches.value.filter(b => stageScopeIds.value.includes(b.id) && pipelineScopeIds.value.includes(b.id))
);

// Otras etapas del pipeline para el selector "mover a otra columna"
const otherStages = computed(() => {
  const stages = props.pipeline.stages || props.pipeline.pipeline_stages || [];
  return stages.filter(s => s.id !== props.stage.id);
});

const lostStage = computed(() =>
  otherStages.value.find(s => s.stage_type === 'lost')
);

function toggleBranchToRemove(id) {
  const idx = branchesToRemove.value.indexOf(id);
  if (idx === -1) branchesToRemove.value.push(id);
  else branchesToRemove.value.splice(idx, 1);
}

const newScopeIds = computed(() => {
  if (mode.value === 'current') {
    return stageScopeIds.value.filter(id => id !== activeBranch.value?.id);
  }
  if (mode.value === 'specific') {
    return stageScopeIds.value.filter(id => !branchesToRemove.value.includes(id));
  }
  return [];
});

const willOrphanAll = computed(() => {
  if (mode.value === 'all') return false;
  if (mode.value === 'current') return newScopeIds.value.length === 0;
  if (mode.value === 'specific') return branchesToRemove.value.length > 0 && newScopeIds.value.length === 0;
  return false;
});

const branchNameById = id => allBranches.value.find(b => b.id === id)?.name || `#${id}`;

const summaryText = computed(() => {
  if (mode.value === 'current') {
    if (!activeBranch.value) return 'Sin sucursal activa';
    return `La columna se ocultará en "${activeBranch.value.name}". Seguirá visible en ${newScopeIds.value.length} sucursal(es).`;
  }
  if (mode.value === 'specific') {
    if (!branchesToRemove.value.length) return 'Selecciona al menos una sucursal a remover';
    const names = branchesToRemove.value.map(branchNameById).join(', ');
    return `Se ocultará en: ${names}. Seguirá visible en ${newScopeIds.value.length} sucursal(es).`;
  }
  return 'La columna se eliminará permanentemente del embudo.';
});

const confirmButtonText = computed(() => {
  if (mode.value === 'current') return `→ Quitar de ${activeBranch.value?.name || 'mi sucursal'}`;
  if (mode.value === 'specific') {
    const n = branchesToRemove.value.length;
    return n === 0 ? '→ Selecciona sucursales' : `→ Quitar de ${n} sucursal${n > 1 ? 'es' : ''}`;
  }
  return '🗑 Eliminar definitivamente';
});

const confirmDisabled = computed(() => {
  if (saving.value) return true;
  if (mode.value === 'current') return !activeBranch.value || !stageScopeIds.value.includes(activeBranch.value.id);
  if (mode.value === 'specific') return branchesToRemove.value.length === 0;
  return confirmName.value.trim() !== (props.stage.name || '').trim();
});

// ¿Mostrar la sección de rescate de leads?
const hasDealsAffected = computed(() => props.dealsInStage > 0);

onMounted(() => {
  store.dispatch('branches/get');
  // Set default targetStageId si elige 'move_to_stage'
  if (otherStages.value.length > 0) targetStageId.value = otherStages.value[0].id;
});

async function confirm() {
  if (confirmDisabled.value) return;
  if (willOrphanAll.value) {
    if (isSuperAdmin.value) {
      mode.value = 'all';
      return;
    }
    // No-super: la columna solo existe en su sucursal; no puede borrarla del
    // embudo entero. Que la elimine un administrador.
    errorMsg.value =
      'Esta columna solo existe en tu sucursal. Pídele a un administrador que la elimine del embudo.';
    return;
  }
  // Defensa: un no-super siempre borra solo de su sucursal activa.
  if (!isSuperAdmin.value) mode.value = 'current';
  saving.value = true;
  errorMsg.value = null;
  try {
    const url = `/api/v1/accounts/${props.accountId}/pipelines/${props.pipelineId}/stages/${props.stage.id}`;
    const payload = {
      lead_action: leadAction.value,
      target_stage_id: leadAction.value === 'move_to_stage' ? targetStageId.value : null,
    };
    if (mode.value !== 'all') {
      payload.scope_branch_ids = newScopeIds.value;
    }
    if (mode.value === 'all') {
      await axios.delete(url, { data: payload });
      emit('deleted', props.stage.id);
    } else {
      const { data } = await axios.delete(url, { data: payload });
      emit('updated', data);
    }
  } catch (e) {
    errorMsg.value = e.response?.data?.message || e.message;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="dsd-overlay" @click.self="emit('close')">
    <div class="dsd-modal" role="dialog" aria-modal="true">
      <!-- HEAD -->
      <div class="dsd-head">
        <div class="dsd-title-wrap">
          <div class="dsd-title-icon">🗑</div>
          <div>
            <div class="dsd-title">Eliminar columna</div>
            <div class="dsd-subtitle">
              "{{ stage.name }}" · {{ visibleBranches.length }} sucursal(es) · {{ dealsInStage }} lead(s)
            </div>
          </div>
        </div>
        <button class="dsd-close" type="button" @click="emit('close')" aria-label="Cerrar">×</button>
      </div>

      <!-- BODY -->
      <div class="dsd-body">
        <div class="dsd-question">¿<strong>Cómo</strong> quieres eliminar esta columna?</div>

        <!-- 3 OPCIONES RADIO · D2 -->
        <div class="dsd-opt-list">
          <div
            class="dsd-opt-card"
            :class="{ 'is-selected': mode === 'current', 'is-soft': mode === 'current' }"
            @click="mode = 'current'"
          >
            <div class="dsd-opt-radio"><div class="dsd-opt-radio-dot"></div></div>
            <div class="dsd-opt-icon">🎯</div>
            <div class="dsd-opt-info">
              <div class="dsd-opt-title">Solo de mi sucursal actual</div>
              <div class="dsd-opt-desc">
                Oculta esta columna SOLO en <strong>{{ activeBranch?.name || '—' }}</strong>. Seguirá visible en las demás.
              </div>
              <div class="dsd-opt-tag soft">Recomendado · seguro</div>
            </div>
          </div>

          <div
            v-if="isSuperAdmin"
            class="dsd-opt-card"
            :class="{ 'is-selected': mode === 'specific', 'is-medium': mode === 'specific' }"
            @click="mode = 'specific'"
          >
            <div class="dsd-opt-radio"><div class="dsd-opt-radio-dot"></div></div>
            <div class="dsd-opt-icon">🏢</div>
            <div class="dsd-opt-info">
              <div class="dsd-opt-title">De algunas sucursales específicas</div>
              <div class="dsd-opt-desc">
                Selecciona en qué sucursales ocultar esta columna. Las que NO marques la conservarán.
              </div>
              <div class="dsd-opt-tag medium">Avanzado · selección múltiple</div>

              <div v-if="mode === 'specific'" class="dsd-branches-grid">
                <div
                  v-for="b in visibleBranches"
                  :key="b.id"
                  class="dsd-br-row"
                  :class="{ 'is-on': branchesToRemove.includes(b.id) }"
                  @click.stop="toggleBranchToRemove(b.id)"
                >
                  <div class="dsd-br-cb">
                    <span v-if="branchesToRemove.includes(b.id)">✓</span>
                  </div>
                  <div class="dsd-br-icon">{{ b.code || b.name?.substring(0, 4).toUpperCase() }}</div>
                  <div class="dsd-br-info">
                    <div class="dsd-br-name">{{ b.name }}</div>
                    <div class="dsd-br-meta">
                      {{ b.id === activeBranch?.id ? 'Tu sucursal activa' : (b.is_personal ? 'Personal' : 'Sucursal') }}
                    </div>
                  </div>
                </div>
                <div v-if="visibleBranches.length === 0" class="dsd-empty-msg">
                  La columna no está activa en ninguna sucursal visible.
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="isSuperAdmin"
            class="dsd-opt-card"
            :class="{ 'is-selected': mode === 'all' }"
            @click="mode = 'all'"
          >
            <div class="dsd-opt-radio"><div class="dsd-opt-radio-dot"></div></div>
            <div class="dsd-opt-icon">💀</div>
            <div class="dsd-opt-info">
              <div class="dsd-opt-title">DEFINITIVAMENTE de TODAS las sucursales</div>
              <div class="dsd-opt-desc">
                La columna se elimina del embudo. <strong>Esta acción NO se puede deshacer.</strong>
              </div>
              <div class="dsd-opt-tag danger">⚠ Irreversible · borra la columna</div>

              <div v-if="mode === 'all'" class="dsd-confirm-input-wrap">
                <div class="dsd-confirm-label">
                  Para confirmar, escribe <strong>{{ stage.name }}</strong> exactamente:
                </div>
                <input
                  v-model="confirmName"
                  class="dsd-confirm-input"
                  placeholder="Escribe el nombre de la columna…"
                  @click.stop
                />
              </div>
            </div>
          </div>
        </div>

        <!-- SECCIÓN LEAD RESCUE · solo si la columna tiene leads -->
        <div v-if="hasDealsAffected" class="dsd-lead-section">
          <div class="dsd-lead-title">
            ⚠ Hay <strong>{{ dealsInStage }}</strong> lead(s) en esta columna · ¿qué hacer con ellos?
          </div>
          <div class="dsd-lead-opts">
            <label class="dsd-lead-opt" :class="{ 'is-on': leadAction === 'move_to_lost' }">
              <input v-model="leadAction" type="radio" value="move_to_lost" />
              <div class="dsd-lead-opt-info">
                <div class="dsd-lead-opt-title">
                  🏆 Mover a la columna PERDIDO
                  <span v-if="!lostStage" class="dsd-lead-warn">⚠ no detectada — crea una con tipo "lost"</span>
                  <span v-else class="dsd-lead-detected">✓ detectada: "{{ lostStage.name }}"</span>
                </div>
                <div class="dsd-lead-opt-desc">Conserva los contactos · default seguro</div>
              </div>
            </label>

            <label class="dsd-lead-opt" :class="{ 'is-on': leadAction === 'move_to_stage' }">
              <input v-model="leadAction" type="radio" value="move_to_stage" />
              <div class="dsd-lead-opt-info">
                <div class="dsd-lead-opt-title">📦 Mover a otra columna</div>
                <select
                  v-if="leadAction === 'move_to_stage'"
                  v-model="targetStageId"
                  class="dsd-target-select"
                  @click.stop
                >
                  <option v-for="s in otherStages" :key="s.id" :value="s.id">
                    {{ s.name }} ({{ s.stage_type }})
                  </option>
                </select>
              </div>
            </label>

            <label class="dsd-lead-opt is-danger" :class="{ 'is-on': leadAction === 'delete' }">
              <input v-model="leadAction" type="radio" value="delete" />
              <div class="dsd-lead-opt-info">
                <div class="dsd-lead-opt-title">🗑 Eliminar también los leads</div>
                <div class="dsd-lead-opt-desc">Destruye los deals · los contactos se conservan en Chatwoot</div>
              </div>
            </label>
          </div>
        </div>

        <!-- Resumen + warn + error -->
        <div v-if="mode !== 'all' || confirmName" class="dsd-summary" :class="{ 'is-danger': mode === 'all' }">
          {{ summaryText }}
        </div>

        <div v-if="willOrphanAll" class="dsd-warn">
          ⚠ Esta operación dejaría la columna sin ninguna sucursal · al confirmar se cambiará a "Eliminar definitivamente".
        </div>

        <div v-if="errorMsg" class="dsd-error">⚠ {{ errorMsg }}</div>
      </div>

      <!-- FOOT -->
      <div class="dsd-foot">
        <button @click="emit('close')" class="dsd-btn dsd-cancel" type="button">Cancelar</button>
        <button @click="confirm" :disabled="confirmDisabled" class="dsd-btn dsd-delete" type="button">
          {{ saving ? '⏳ Procesando…' : confirmButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
/* DeleteStageDialog · D2 · cristal negro + rojo + lead rescue */
.dsd-overlay { position: fixed; inset: 0; z-index: 60; background: rgba(0,0,0,0.78); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; padding: 24px; font-family: -apple-system, system-ui, sans-serif; }
.dsd-modal { width: 580px; max-width: 100%; max-height: calc(100vh - 48px); background: rgba(10,10,10,0.98); border: 1.5px solid rgba(239,68,68,0.45); border-radius: 18px; overflow: hidden; box-shadow: 0 30px 90px rgba(0,0,0,0.85), 0 0 50px rgba(239,68,68,0.18); display: flex; flex-direction: column; position: relative; color: #fff; }
.dsd-modal::before { content: ''; position: absolute; inset: 0; pointer-events: none; border-radius: 18px; background: linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%), radial-gradient(circle at 100% 0%, rgba(239,68,68,0.10), transparent 60%); z-index: 1; }
.dsd-modal > * { position: relative; z-index: 2; }

.dsd-head { padding: 18px 22px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: space-between; align-items: center; background: radial-gradient(ellipse at 0% 0%, rgba(239,68,68,0.10), transparent 60%); flex-shrink: 0; }
.dsd-title-wrap { display: flex; align-items: center; gap: 12px; }
.dsd-title-icon { width: 38px; height: 38px; border-radius: 10px; background: linear-gradient(135deg, rgba(239,68,68,0.20), rgba(220,38,38,0.10)); border: 1.5px solid rgba(239,68,68,0.40); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.dsd-title { font-size: 18px; font-weight: 900; color: #fff; line-height: 1.15; }
.dsd-subtitle { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 1px; }
.dsd-close { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); color: rgba(255,255,255,0.55); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 16px; line-height: 1; padding: 0; font-family: inherit; transition: all 0.15s; }
.dsd-close:hover { background: rgba(239,68,68,0.20); color: #fff; border-color: rgba(239,68,68,0.40); transform: rotate(90deg); }

.dsd-body { padding: 18px 22px; flex: 1; overflow-y: auto; }
.dsd-question { font-size: 12px; color: rgba(255,255,255,0.85); margin-bottom: 14px; line-height: 1.5; }
.dsd-question strong { color: #f87171; }

.dsd-opt-list { display: flex; flex-direction: column; gap: 10px; }
.dsd-opt-card { padding: 14px 16px; background: rgba(255,255,255,0.03); border: 2px solid rgba(255,255,255,0.08); border-radius: 12px; cursor: pointer; transition: all 0.18s; display: flex; gap: 14px; align-items: flex-start; }
.dsd-opt-card:hover { border-color: rgba(255,255,255,0.20); transform: translateY(-1px); }
.dsd-opt-card.is-selected { background: linear-gradient(135deg, rgba(239,68,68,0.10), rgba(220,38,38,0.04)); border-color: rgba(239,68,68,0.55); box-shadow: 0 0 24px rgba(239,68,68,0.20); }
.dsd-opt-card.is-selected.is-soft { background: linear-gradient(135deg, rgba(99,102,241,0.10), rgba(67,56,202,0.04)); border-color: rgba(99,102,241,0.55); box-shadow: 0 0 24px rgba(99,102,241,0.20); }
.dsd-opt-card.is-selected.is-medium { background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(217,119,6,0.04)); border-color: rgba(251,191,36,0.55); box-shadow: 0 0 24px rgba(251,191,36,0.20); }

.dsd-opt-radio { width: 22px; height: 22px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.25); flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; }
.dsd-opt-card.is-selected .dsd-opt-radio { border-color: #ef4444; }
.dsd-opt-card.is-selected.is-soft .dsd-opt-radio { border-color: #818cf8; }
.dsd-opt-card.is-selected.is-medium .dsd-opt-radio { border-color: #fbbf24; }
.dsd-opt-radio-dot { width: 10px; height: 10px; border-radius: 50%; background: transparent; }
.dsd-opt-card.is-selected .dsd-opt-radio-dot { background: #ef4444; }
.dsd-opt-card.is-selected.is-soft .dsd-opt-radio-dot { background: #818cf8; }
.dsd-opt-card.is-selected.is-medium .dsd-opt-radio-dot { background: #fbbf24; }

.dsd-opt-icon { width: 38px; height: 38px; border-radius: 10px; background: rgba(255,255,255,0.04); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
.dsd-opt-card.is-selected .dsd-opt-icon { background: rgba(239,68,68,0.18); }
.dsd-opt-card.is-selected.is-soft .dsd-opt-icon { background: rgba(99,102,241,0.18); }
.dsd-opt-card.is-selected.is-medium .dsd-opt-icon { background: rgba(251,191,36,0.18); }

.dsd-opt-info { flex: 1; min-width: 0; }
.dsd-opt-title { font-size: 14px; font-weight: 900; color: #fff; }
.dsd-opt-card.is-selected.is-soft .dsd-opt-title { color: #818cf8; }
.dsd-opt-card.is-selected.is-medium .dsd-opt-title { color: #fbbf24; }
.dsd-opt-card.is-selected:not(.is-soft):not(.is-medium) .dsd-opt-title { color: #f87171; }
.dsd-opt-desc { font-size: 11px; color: rgba(255,255,255,0.65); margin-top: 4px; line-height: 1.5; }
.dsd-opt-desc strong { color: #fff; }

.dsd-opt-tag { display: inline-block; padding: 3px 8px; border-radius: 5px; font-size: 9px; font-weight: 800; letter-spacing: 0.5px; margin-top: 6px; text-transform: uppercase; }
.dsd-opt-tag.soft { background: rgba(99,102,241,0.15); color: #818cf8; border: 1px solid rgba(99,102,241,0.40); }
.dsd-opt-tag.medium { background: rgba(251,191,36,0.15); color: #fbbf24; border: 1px solid rgba(251,191,36,0.40); }
.dsd-opt-tag.danger { background: rgba(239,68,68,0.15); color: #f87171; border: 1px solid rgba(239,68,68,0.40); }

/* Multi-checkbox de sucursales */
.dsd-branches-grid { display: flex; flex-direction: column; gap: 5px; margin-top: 12px; }
.dsd-br-row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; background: rgba(255,255,255,0.03); border: 1.5px solid rgba(255,255,255,0.08); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
.dsd-br-row:hover { border-color: rgba(239,68,68,0.30); }
.dsd-br-row.is-on { background: linear-gradient(135deg, rgba(239,68,68,0.10), rgba(220,38,38,0.04)); border-color: rgba(239,68,68,0.55); box-shadow: 0 0 12px rgba(239,68,68,0.18); }
.dsd-br-cb { width: 20px; height: 20px; border-radius: 5px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(255,255,255,0.18); flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #fff; font-weight: 900; }
.dsd-br-row.is-on .dsd-br-cb { background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; }
.dsd-br-icon { width: 28px; height: 28px; border-radius: 6px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08); display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 900; color: #fbbf24; flex-shrink: 0; letter-spacing: 0.3px; }
.dsd-br-row.is-on .dsd-br-icon { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.40); color: #f87171; }
.dsd-br-info { flex: 1; min-width: 0; }
.dsd-br-name { font-size: 12px; font-weight: 800; color: #fff; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.dsd-br-row.is-on .dsd-br-name { color: #f87171; text-decoration: line-through; text-decoration-color: rgba(239,68,68,0.50); }
.dsd-br-meta { font-size: 10px; color: rgba(255,255,255,0.45); margin-top: 1px; }
.dsd-empty-msg { padding: 12px; font-size: 11px; color: rgba(255,255,255,0.45); text-align: center; font-style: italic; }

/* Confirmación con input */
.dsd-confirm-input-wrap { margin-top: 12px; }
.dsd-confirm-label { font-size: 11px; color: rgba(255,255,255,0.65); margin-bottom: 6px; }
.dsd-confirm-label strong { color: #f87171; font-family: ui-monospace, monospace; padding: 2px 6px; background: rgba(239,68,68,0.10); border-radius: 4px; }
.dsd-confirm-input { width: 100%; padding: 10px 12px; background: rgba(255,255,255,0.04); border: 1.5px solid rgba(239,68,68,0.40); border-radius: 7px; color: #fff; font-size: 13px; font-family: ui-monospace, monospace; outline: none; }
.dsd-confirm-input:focus { border-color: #ef4444; box-shadow: 0 0 0 3px rgba(239,68,68,0.15); }
.dsd-confirm-input::placeholder { color: rgba(255,255,255,0.30); }

/* Sección de leads (rescue) */
.dsd-lead-section { margin-top: 18px; padding: 14px; background: rgba(251,191,36,0.04); border: 1.5px solid rgba(251,191,36,0.30); border-radius: 11px; }
.dsd-lead-title { font-size: 12px; font-weight: 800; color: #fbbf24; margin-bottom: 10px; line-height: 1.4; }
.dsd-lead-title strong { color: #fff; }
.dsd-lead-opts { display: flex; flex-direction: column; gap: 8px; }
.dsd-lead-opt { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; background: rgba(255,255,255,0.03); border: 1.5px solid rgba(255,255,255,0.08); border-radius: 9px; cursor: pointer; transition: all 0.15s; }
.dsd-lead-opt:hover { border-color: rgba(34,197,94,0.30); }
.dsd-lead-opt.is-on { background: linear-gradient(135deg, rgba(34,197,94,0.10), rgba(22,163,74,0.04)); border-color: rgba(34,197,94,0.50); box-shadow: 0 0 12px rgba(34,197,94,0.18); }
.dsd-lead-opt.is-danger:hover { border-color: rgba(239,68,68,0.30); }
.dsd-lead-opt.is-danger.is-on { background: linear-gradient(135deg, rgba(239,68,68,0.10), rgba(220,38,38,0.04)); border-color: rgba(239,68,68,0.50); box-shadow: 0 0 12px rgba(239,68,68,0.18); }
.dsd-lead-opt input[type="radio"] { margin-top: 4px; accent-color: #4ade80; flex-shrink: 0; }
.dsd-lead-opt.is-danger input[type="radio"] { accent-color: #f87171; }
.dsd-lead-opt-info { flex: 1; min-width: 0; }
.dsd-lead-opt-title { font-size: 12px; font-weight: 800; color: #fff; }
.dsd-lead-opt-desc { font-size: 10px; color: rgba(255,255,255,0.55); margin-top: 2px; line-height: 1.4; }
.dsd-lead-warn { font-size: 9px; color: #fbbf24; font-weight: 700; margin-left: 6px; }
.dsd-lead-detected { font-size: 9px; color: #4ade80; font-weight: 700; margin-left: 6px; }
.dsd-target-select { margin-top: 6px; width: 100%; padding: 7px 10px; background: rgba(255,255,255,0.04); border: 1px solid rgba(34,197,94,0.40); border-radius: 6px; color: #fff; font-size: 11px; font-family: inherit; outline: none; }
.dsd-target-select option { background: #0a0a0a; color: #fff; }

.dsd-summary { margin-top: 14px; padding: 11px 14px; background: rgba(239,68,68,0.06); border: 1px dashed rgba(239,68,68,0.40); border-radius: 9px; font-size: 11px; color: #f87171; line-height: 1.5; }
.dsd-summary.is-danger { background: rgba(239,68,68,0.12); border-color: rgba(239,68,68,0.55); border-style: solid; }
.dsd-warn { margin-top: 10px; padding: 10px 12px; background: rgba(245,158,11,0.10); border: 1px solid rgba(245,158,11,0.40); border-radius: 8px; font-size: 11px; color: #fbbf24; line-height: 1.5; }
.dsd-error { margin-top: 14px; padding: 10px 12px; background: rgba(239,68,68,0.10); border: 1px solid rgba(239,68,68,0.40); border-radius: 8px; font-size: 11px; color: #f87171; font-weight: 700; }

.dsd-foot { padding: 14px 22px; border-top: 1px solid rgba(255,255,255,0.06); display: flex; justify-content: flex-end; gap: 8px; background: rgba(0,0,0,0.4); flex-shrink: 0; }
.dsd-btn { padding: 10px 18px; border-radius: 8px; font-size: 12px; font-weight: 800; cursor: pointer; border: none; font-family: inherit; }
.dsd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.dsd-cancel { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.08); }
.dsd-cancel:hover { background: rgba(255,255,255,0.08); }
.dsd-delete { background: linear-gradient(135deg, #ef4444, #dc2626); color: #fff; box-shadow: 0 0 16px rgba(239,68,68,0.30); }
.dsd-delete:hover:not(:disabled) { box-shadow: 0 0 24px rgba(239,68,68,0.55); }
</style>
